/**
 * B8 — module-level cursor-world snapshot bridge.
 *
 * Mirrors the design of `scene3d/cameraSnapshot.ts`. The R3F-managed
 * `<Canvas>` cannot host DOM children (the reconciler crashes on
 * non-THREE nodes), so the Dynamic Input overlay is a sibling DOM
 * subtree that needs the live cursor in world coordinates without
 * re-rendering on every pointermove event.
 *
 * # Producer
 *
 *   Scene3DStage's `onCanvasHover` wrapper calls `setCursorWorld` on
 *   every pointermove the canvas observes plus a `null` on
 *   pointerleave. The signal already crosses the R3F boundary into
 *   plain React land so this module can publish without crossing the
 *   <Canvas> boundary itself.
 *
 * # Consumer
 *
 *   `DynInputOverlay` subscribes via `useSyncExternalStore` (or polls
 *   inside a `useFrame`-driven rAF loop) and reads the cursor on each
 *   render to project it through the camera snapshot.
 *
 * # Why a module-level ref (vs. zustand / React state)
 *
 *   Pointermove fires at ~60–120 Hz; routing each event through React
 *   state would burn ~1 ms of reconciliation per frame and saturate
 *   the audit transcript. The ref is mutable, the version counter is
 *   monotonic, and `subscribe` lets consumers throttle to camera-tick
 *   cadence via `useSyncExternalStore`.
 */

export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

const state = {
  cursor: null as WorldPoint | null,
  version: 0,
  listeners: new Set<() => void>(),
};

/**
 * Update the live cursor world position and notify subscribers.
 * Producers MUST call with `null` on pointerleave so consumers can
 * hide the overlay.
 */
export function setCursorWorldSnapshot(point: WorldPoint | null): void {
  // Cheap pointer-equality short-circuit — bail when cursor truly hasn't
  // moved (consecutive identical writes from snap-magnet, polar lock).
  const prev = state.cursor;
  if (
    prev !== null &&
    point !== null &&
    prev.x === point.x &&
    prev.y === point.y
  ) {
    return;
  }
  if (prev === null && point === null) return;
  state.cursor = point ? { x: point.x, y: point.y } : null;
  state.version += 1;
  for (const listener of state.listeners) listener();
}

/** Read the current world cursor. `null` until the first pointermove. */
export function getCursorWorldSnapshot(): WorldPoint | null {
  return state.cursor;
}

/** Monotonic counter, increments on every `setCursorWorldSnapshot`. */
export function getCursorWorldVersion(): number {
  return state.version;
}

/**
 * Register a listener; returns cleanup. Wired into
 * `useSyncExternalStore`'s subscribe contract.
 */
export function subscribeCursorWorld(listener: () => void): () => void {
  state.listeners.add(listener);
  return () => {
    state.listeners.delete(listener);
  };
}

/** Test-only reset hook. Production code never calls this. */
export function __resetCursorWorldSnapshotForTests(): void {
  state.cursor = null;
  state.version = 0;
  state.listeners.clear();
}
